import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    topSectionRichContent = "topSectionRichContent",
    topSectionExpertImage = "topSectionExpertImage",
    experienceTitle = "experienceTitle",
    mobileNumber = "mobileNumber",
    timeSlot = "timeSlot",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.topSectionRichContent]: string;
        [fields.topSectionExpertImage]: string;
        [fields.experienceTitle]: string;
        [fields.mobileNumber]: string;
        [fields.timeSlot]: string;
    };
}

export interface LookingExpertPDPStyles {
    image?: LazyImageProps;
    topsectionTitle?: TypographyPresentationProps;
    subheadingTopsection?: TypographyPresentationProps;
    imgWrapper?: InjectableCss;
    topSectionHeadingWrapper?: InjectableCss;
    imageContent?: InjectableCss;
}

export const lookingExpertPDPStyles: LookingExpertPDPStyles = {
    image: {
        css: css`
            aspect-ratio: 1/1;
            object-fit: contain;
            max-width: 104px;
            max-height: 104px;
            height: auto;

            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            max-height: max-content;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    topsectionTitle: {
        css: css`
            font-weight: 600;
            font-size: 24px;
            line-height: 32px;
            color: #262525;
            display: block;
            margin-bottom: 4px;
        `
    },
    subheadingTopsection: {
        css: css`
            & p {
                font-size: 18px;
                color: #262525;
                line-height: 28px;
            }
        `
    },
    imgWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            gap: 24px;
            flex: 0 0 auto;
        `
    },
    imageContent: {
        css: css`
            display: flex;
            justify-content: space-between;
            color: #262525;
            flex-direction: column;
            width: 100%;

            span:nth-child(1) {
                font-size: 18px;
                line-height: 24px;
                font-weight: 600;
            }

            span:nth-child(2) {
                font-size: 18px;
                line-height: 24px;
                font-weight: 700;
                color: #0F96D9;
            }

            span:nth-child(3) {
                font-size: 14px;
                line-height: 20px;
                font-weight: 400;
            }
        `
    },
    topSectionHeadingWrapper: {
        css: css`
             ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css``,
                        css`
                            flex-direction: column;
                            align-items: start !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `
    }
};


type Props = OwnProps

const styles = lookingExpertPDPStyles;
const CENBrandLookingForExpert: React.FC<Props> = ({ fields }) => {

    const topSectionContainer = {
        css: css`
            background: #F2F2F2;
            height: 100%;
            padding: 24px 40px;
            margin: 40px 20px;
            display: flex;
            align-items: center;
            gap: 24px;
            justify-content: space-around;
            @media all and (max-width: 767px){
                flex-direction: column;
                padding: 24px 16px;
                margin: 24px 0;
                align-items: flex-start;
            }
        `
    }

    return (
        <StyledWrapper {...topSectionContainer}>
            <StyledWrapper {...styles.topSectionHeadingWrapper}>
                {fields.title && (
                    <Typography  {...styles.topsectionTitle}>
                        {fields.title}
                    </Typography>
                )}
                <Typography {...styles.subheadingTopsection}>{parse(transformContentHtml(fields.topSectionRichContent), parserOptions)}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.imgWrapper}>
                <LazyImage {...styles.image} src={fields.topSectionExpertImage} altText={fields.title} />
                <StyledWrapper {...styles.imageContent}>
                    <Typography>
                        {fields.experienceTitle}
                    </Typography>
                    <Typography>
                        <StyledWrapper as="a" href={`tel: ${fields.mobileNumber}`}>{fields.mobileNumber}</StyledWrapper>
                    </Typography>
                    <Typography>
                        {fields.timeSlot}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENBrandLookingForExpert,
    definition: {
        group: "Common",
        displayName: "CEN Brand Looking For Expert",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.topSectionRichContent,
                displayName: "Content",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.topSectionExpertImage,
                displayName: "Expert Image",
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: fields.experienceTitle,
                displayName: "Experience Title",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.mobileNumber,
                displayName: "Mobile Number",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.timeSlot,
                displayName: "Time Slot",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;